import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Card } from "@amzn/stencil-react-components/card";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import {
  IconCheckCircleFill,
  IconSize,
} from "@amzn/stencil-react-components/icons";
import { StatusEnum } from "../../../hoc/with-status";
import { Text } from "../../typography";
import { addEventMetricWithBBId } from "../../../utility/adobe-analytics";
import { useNavigatorWithURLReadyOnOpeningNewTab } from "../../../hooks/use-navigator";
import useArb from "../../../hooks/use-arb";
import { statusCompleteCheckColor } from "../../../config/palette";
import { renderBGColor } from "../../task/ui-redesign";

interface ZapposPurchasePromotionProps {
  status: StatusEnum;
  taskId: string;
}
export const ZapposPurchasePromotion = ({
  status,
  taskId,
}: ZapposPurchasePromotionProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const discountInfo = bundle.getMessage(
    "Checklist-MindYourStep-Contigencies-Completion-DiscountInfo"
  );
  const statusTitle = bundle.getMessage("checklist-General-Status");
  const detailsTitle = bundle.getMessage(
    "Checklist-CardControlled-DetailsTitle"
  );
  const detailsText1 = bundle.getMessage(
    "Checklist-MindYourStep-Details-Text1"
  );
  const detailsText2 = bundle.getMessage(
    "Checklist-MindYourStep-Details-Text2"
  );
  const actionRequired = bundle.getMessage(
    "checklist-General-TaskStatus-ActionRequired"
  );
  const completed = bundle.getMessage("checklist-General-TaskStatus-Completed");
  const dueSoon = bundle.getMessage("checklist-General-TaskStatus-DueSoon");

  const styles = {
    border: "2px dashed rgba(1, 1, 1, 1)",
    color: "black",
    lineHeight: "2.5",
    background: "white",
    width: "100px",
    fontSize: "20px",
  };

  const boxStyle = {
    background: "#D7F3ED",
    padding: "10px",
    border: "1px solid green",
    color: "#008574",
    height: "90px",
    textAlign: "center" as const,
    borderLeft: "8px solid green",
  };

  if (status == StatusEnum.COMPLETED) {
    return (
      <Col className="flyout-content">
        <Col gridGap="1rem">
          <Card padding="0rem" title={statusTitle}>
            <Row>
              <Col backgroundColor={renderBGColor(status)} width={"6px"}></Col>
              <Col gridGap={"1rem"} padding={"1rem"}>
                <Text fontSize={"T400"}>{statusTitle}</Text>
                <Row alignItems={"center"} gridGap={"0.5rem"}>
                  <IconCheckCircleFill
                    size={IconSize.Medium}
                    color={statusCompleteCheckColor}
                  />
                  <Text>{completed}</Text>
                </Row>
              </Col>
            </Row>
          </Card>
          <Card>
            <Col gridGap={"1rem"}>
              <Text fontSize={"T400"}>{detailsTitle}</Text>
              <Text>{detailsText2}</Text>
            </Col>
          </Card>
        </Col>
      </Col>
    );
  }

  return (
    <Col className="flyout-content">
      <Col gridGap="1rem">
        <Card padding="0rem" title={statusTitle}>
          <Row>
            <Col backgroundColor={renderBGColor(status)} width={"6px"}></Col>
            <Col gridGap={"1rem"} padding={"1rem"}>
              <Text fontSize={"T400"}>{statusTitle}</Text>
              <Col gridGap="0.5rem">
                <Row alignItems={"center"}>
                  <Text>{actionRequired}</Text>
                </Row>
                <Row alignItems={"center"}>
                  <Text>{dueSoon}</Text>
                </Row>
              </Col>
            </Col>
          </Row>
        </Card>
        <Text>
          <div style={boxStyle}>
            <strong>
              {discountInfo}
              <br />
            </strong>
            <span style={styles}>{taskId}</span>
          </div>
        </Text>
        <Card>
          <Col gridGap={"1rem"}>
            <Text fontSize={"T400"}>{detailsTitle}</Text>
            <Text>{detailsText1}</Text>
          </Col>
        </Card>
      </Col>
    </Col>
  );
};

interface ZapposPurchasePromotionButtonProps {
  href: string;
  taskId: string;
}
export const ZapposPurchasePromotionButton = ({
  href,
  taskId,
}: ZapposPurchasePromotionButtonProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const purchaseButtonText = bundle.getMessage(
    "Checklist-MindYourStep-Contigencies-Completion-Purchase-ButtonText"
  );

  const { applicationId } = useParams<{
    applicationId: string;
  }>();

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      await addEventMetricWithBBId(window, applicationId, eventName);
    },
    [applicationId]
  );

  const trackZapposPurchaseClick = useCallback(
    () => sendAnalyticsEvent("Make Shoe Purchase"),
    [sendAnalyticsEvent]
  );

  const navigateToUrl = useNavigatorWithURLReadyOnOpeningNewTab(href);

  const navigateToZappos = useCallback(() => {
    sendAnalyticsEvent("Make Shoe Purchase");
    copyTextToClipboard(taskId);
    navigateToUrl();
  }, [trackZapposPurchaseClick, navigateToUrl, taskId]);

  return (
    <Button
      size={ButtonSize.Small}
      variant={ButtonVariant.Primary}
      onClick={navigateToZappos}
    >
      {purchaseButtonText}
    </Button>
  );
};

async function copyTextToClipboard(text: string) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  await navigator.clipboard.writeText(text).then(
    function () {},
    function (err) {
      console.error("Unable to async copy to clipboard: ", err);
    }
  );
}

function fallbackCopyTextToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("Unable to fallback copy to clipboard: ", err);
  }

  document.body.removeChild(textArea);
}
